<template>
  <div class="table-owner-cell">
    <BittsSelect
      :model-value="selectedUserId"
      :options="ownerOptions"
      :has-opt-groups="hasOwner"
      :searchable="true"
      :disabled="!canEditOwner"
      placeholder="Assign"
      size="small"
      dropdown-class-name="table-owner-cell__dropdown"
      :dropdown-match-select-width="false"
      class="table-owner-cell__select"
      aria-label="Owner"
      @change="handleOwnerChange"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsSelect } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed } from 'vue';

import { useForwardCellFocus } from '@/composables/useForwardCellFocus';
import { useTeamStore } from '@/stores';
import { buildOwnerOptions } from '@/utils/ownerOptions';

import type { TableCellOwner, TableCellOwnerOption } from './types';

const { params } = defineProps<{
  params: ICellRendererParams;
}>();

useForwardCellFocus(params.eGridCell);

const teamStore = useTeamStore();

const UNKNOWN_OWNER_LABEL = 'Unknown user';
/** The select treats a `null` value as "nothing selected", so it cannot back an option. */
const NO_OWNER_VALUE = -1;
const NO_OWNER_OPTION: TableCellOwnerOption = {
  value: NO_OWNER_VALUE,
  label: 'No owner',
};

const owner = computed(() => (params.value ?? null) as TableCellOwner);
const selectedUserId = computed(() => owner.value?.user_id ?? null);
const hasOwner = computed(() => !!owner.value);

const canEditOwner = computed<boolean>(
  () => params.context?.canEditOwner?.() ?? true,
);

/**
 * The option list is identical for every row, so a table with many rows should
 * build it once and hand it over through the grid context. Falling back to the
 * store keeps tables that have not done so working.
 */
const teamOptions = computed<TableCellOwnerOption[]>(
  () =>
    params.context?.teamOwnerOptions?.() ??
    buildOwnerOptions(teamStore.coreUsers),
);

const assignableOptions = computed(() => {
  const current = owner.value;
  if (!current) return teamOptions.value;
  // The owner may not be a core user (e.g. a deactivated seat), so append them
  // to keep the select showing a name instead of a raw id.
  const isTeamMember = teamOptions.value.some(
    (option) => option.value === current.user_id,
  );
  return isTeamMember
    ? teamOptions.value
    : [
        ...teamOptions.value,
        { value: current.user_id, label: current.name ?? UNKNOWN_OWNER_LABEL },
      ];
});

/** Headingless groups make BittsSelect draw a divider under "No owner". */
const ownerOptions = computed(() =>
  hasOwner.value
    ? [
        { heading: null, items: [NO_OWNER_OPTION] },
        { heading: null, items: assignableOptions.value },
      ]
    : assignableOptions.value,
);

function handleOwnerChange(userId: number) {
  params.context?.updateRowOwner?.(
    params.data?.id,
    userId === NO_OWNER_VALUE ? null : userId,
  );
}
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.table-owner-cell {
  @apply flex items-center h-full w-full min-w-0;
}
.table-owner-cell__select {
  @apply min-w-[100px] max-w-full;

  &.bitts-select
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply h-24 min-h-[24px] rounded-6 shadow-none;
  }

  &.bitts-select .ant-select-selection-item {
    @apply text-sm leading-[22px];
  }

  &.bitts-select .ant-select-selection-placeholder {
    @apply text-sm leading-[22px];
  }

  &.bitts-select .ant-select-arrow {
    @apply right-6;
  }
}
.table-owner-cell__dropdown {
  @apply min-w-[200px];
}
</style>
